/**
 * Google Routes API Provider
 *
 * Implements the RoutingProvider interface using Google's Routes API (v2).
 *
 * API Documentation: https://developers.google.com/maps/documentation/routes/compute_route_directions
 *
 * Features:
 * - Route computation with distance, duration, polyline
 * - Toll estimation with currency and breakdown
 * - Traffic-aware routing
 * - Route preferences (avoid tolls, highways, ferries)
 * - Waypoint support
 * - Distance matrix computation
 */

import type {
  RoutingProvider,
  ComputeRouteRequest,
  ComputeRouteResult,
  TollEstimate,
  TollBreakdownItem,
  RouteLeg,
  RouteStep,
  DistanceMatrixRequest,
  DistanceMatrixResult,
  DistanceMatrixEntry,
  TravelMode,
} from '../../types/routing'
import type { LatLng } from '../../types/common'
import { RoutingError } from '../../types/common'
import type { GoogleClient } from './client'

const ROUTES_API_URL =
  'https://routes.googleapis.com/directions/v2:computeRoutes'
const DISTANCE_MATRIX_API_URL =
  'https://routes.googleapis.com/distanceMatrix/v2:computeRouteMatrix'

/** Google Routes API request structure */
interface GoogleRoutesRequest {
  origin: GoogleWaypoint
  destination: GoogleWaypoint
  intermediates?: GoogleWaypoint[]
  travelMode: GoogleTravelMode
  routingPreference?: GoogleRoutingPreference
  routeModifiers?: GoogleRouteModifiers
  departureTime?: string
  arrivalTime?: string
  computeAlternativeRoutes?: boolean
  languageCode?: string
  units?: 'METRIC' | 'IMPERIAL'
  extraComputations?: GoogleExtraComputation[]
}

interface GoogleWaypoint {
  location?: {
    latLng: { latitude: number; longitude: number }
  }
  placeId?: string
  address?: string
}

type GoogleTravelMode = 'DRIVE' | 'WALK' | 'BICYCLE' | 'TWO_WHEELER' | 'TRANSIT'

type GoogleRoutingPreference =
  | 'TRAFFIC_UNAWARE'
  | 'TRAFFIC_AWARE'
  | 'TRAFFIC_AWARE_OPTIMAL'

interface GoogleRouteModifiers {
  avoidTolls?: boolean
  avoidHighways?: boolean
  avoidFerries?: boolean
  avoidIndoor?: boolean
  vehicleInfo?: {
    emissionType?: 'GASOLINE' | 'ELECTRIC' | 'HYBRID' | 'DIESEL'
  }
  tollPasses?: string[]
}

type GoogleExtraComputation = 'TOLLS' | 'FUEL_CONSUMPTION' | 'TRAFFIC_ON_POLYLINE'

/** Google Routes API response structure */
interface GoogleRoutesResponse {
  routes?: GoogleRoute[]
  fallbackInfo?: {
    routingMode: string
    reason: string
  }
  error?: {
    code: number
    message: string
    status: string
  }
}

interface GoogleRoute {
  distanceMeters: number
  duration: string // e.g., "3600s"
  staticDuration?: string
  polyline?: { encodedPolyline: string }
  description?: string
  warnings?: string[]
  viewport?: {
    low: { latitude: number; longitude: number }
    high: { latitude: number; longitude: number }
  }
  travelAdvisory?: {
    tollInfo?: GoogleTollInfo
    speedReadingIntervals?: unknown[]
    fuelConsumptionMicroliters?: string
  }
  legs?: GoogleRouteLeg[]
  routeLabels?: string[]
  routeToken?: string
}

interface GoogleTollInfo {
  estimatedPrice?: GoogleMoney[]
}

interface GoogleMoney {
  currencyCode: string
  units?: string
  nanos?: number
}

interface GoogleRouteLeg {
  distanceMeters: number
  duration: string
  staticDuration?: string
  polyline?: { encodedPolyline: string }
  startLocation?: { latLng: { latitude: number; longitude: number } }
  endLocation?: { latLng: { latitude: number; longitude: number } }
  steps?: GoogleRouteStep[]
  travelAdvisory?: {
    tollInfo?: GoogleTollInfo
  }
}

interface GoogleRouteStep {
  distanceMeters: number
  duration: string
  staticDuration?: string
  polyline?: { encodedPolyline: string }
  startLocation?: { latLng: { latitude: number; longitude: number } }
  endLocation?: { latLng: { latitude: number; longitude: number } }
  navigationInstruction?: {
    maneuver?: string
    instructions?: string
  }
  travelMode?: GoogleTravelMode
}

/** Google Route Matrix response */
interface GoogleRouteMatrixResponse {
  distanceMeters?: number
  duration?: string
  staticDuration?: string
  status?: {
    code: number
    message?: string
  }
  condition?: 'ROUTE_EXISTS' | 'ROUTE_NOT_FOUND'
  originIndex: number
  destinationIndex: number
}

export class GoogleRoutesProvider implements RoutingProvider {
  readonly name = 'google_routes'
  private client: GoogleClient

  constructor(client: GoogleClient) {
    this.client = client
  }

  async computeRoute(request: ComputeRouteRequest): Promise<ComputeRouteResult> {
    const body = this.buildRequestBody(request)
    const fieldMask = this.buildFieldMask(request)

    // Routes API uses X-Goog-FieldMask header instead of query params
    const response = await this.client.post<GoogleRoutesResponse>(
      ROUTES_API_URL,
      body,
      {
        headers: {
          'X-Goog-FieldMask': fieldMask,
        },
      }
    )

    this.validateResponse(response, request)

    return this.mapToCanonical(response.routes![0], request)
  }

  async computeDistanceMatrix(
    request: DistanceMatrixRequest
  ): Promise<DistanceMatrixResult> {
    const body = this.buildMatrixRequestBody(request)

    // Route Matrix API returns streaming responses, we need to handle them
    const responses = await this.client.post<GoogleRouteMatrixResponse[]>(
      DISTANCE_MATRIX_API_URL,
      body,
      {
        headers: {
          'X-Goog-FieldMask':
            'originIndex,destinationIndex,distanceMeters,duration,staticDuration,status,condition',
        },
      }
    )

    return this.mapMatrixToCanonical(
      responses,
      request.origins.length,
      request.destinations.length
    )
  }

  private buildRequestBody(request: ComputeRouteRequest): GoogleRoutesRequest {
    const body: GoogleRoutesRequest = {
      origin: this.buildWaypoint(request.origin),
      destination: this.buildWaypoint(request.destination),
      travelMode: this.mapTravelMode(request.travelMode),
    }

    // Add intermediate waypoints
    if (request.waypoints?.length) {
      body.intermediates = request.waypoints.map((wp) => this.buildWaypoint(wp))
    }

    // Set routing preference
    body.routingPreference = this.getRoutingPreference(request)

    // Build route modifiers
    const modifiers = this.buildRouteModifiers(request)
    if (Object.keys(modifiers).length > 0) {
      body.routeModifiers = modifiers
    }

    // Add departure time
    if (request.departureTime) {
      body.departureTime =
        typeof request.departureTime === 'string'
          ? request.departureTime
          : request.departureTime.toISOString()
    }

    // Request extra computations for tolls
    if (request.includeTolls) {
      body.extraComputations = ['TOLLS']
    }

    return body
  }

  private buildWaypoint(location: LatLng): GoogleWaypoint {
    return {
      location: {
        latLng: {
          latitude: location.latitude,
          longitude: location.longitude,
        },
      },
    }
  }

  private mapTravelMode(mode?: TravelMode): GoogleTravelMode {
    switch (mode) {
      case 'WALK':
        return 'WALK'
      case 'BICYCLE':
        return 'BICYCLE'
      case 'TWO_WHEELER':
        return 'TWO_WHEELER'
      default:
        return 'DRIVE'
    }
  }

  private getRoutingPreference(
    request: ComputeRouteRequest
  ): GoogleRoutingPreference {
    // If shortest distance requested, use traffic-unaware
    if (request.routePreference === 'SHORTEST') {
      return 'TRAFFIC_UNAWARE'
    }

    // If departure time provided, use traffic-aware optimal for predictive traffic
    if (request.departureTime) {
      return 'TRAFFIC_AWARE_OPTIMAL'
    }

    // Default to traffic-unaware: uses average time-independent traffic conditions
    // across the road network. This matches the old Distance Matrix API default
    // and produces stable durations suitable for cached lane data.
    return 'TRAFFIC_UNAWARE'
  }

  private buildRouteModifiers(
    request: ComputeRouteRequest
  ): GoogleRouteModifiers {
    const modifiers: GoogleRouteModifiers = {}

    if (request.avoidTolls) {
      modifiers.avoidTolls = true
    }

    if (request.avoidHighways) {
      modifiers.avoidHighways = true
    }

    if (request.avoidFerries) {
      modifiers.avoidFerries = true
    }

    // Add vehicle info for more accurate toll pricing
    if (request.vehicleInfo?.emissionType) {
      modifiers.vehicleInfo = {
        emissionType: request.vehicleInfo.emissionType,
      }
    }

    // If vehicle has toll transponder, add common passes
    if (request.vehicleInfo?.hasTollTransponder) {
      modifiers.tollPasses = ['US_CA_FASTRAK', 'US_TX_TXTAG', 'US_MA_EZPASSMA']
    }

    return modifiers
  }

  private buildFieldMask(request: ComputeRouteRequest): string {
    const fields = [
      'routes.distanceMeters',
      'routes.duration',
      'routes.staticDuration',
      'routes.description',
      'routes.warnings',
    ]

    if (request.includePolyline) {
      fields.push('routes.polyline.encodedPolyline')
    }

    if (request.includeTolls) {
      fields.push('routes.travelAdvisory.tollInfo')
    }

    if (request.includeSteps) {
      fields.push('routes.legs.steps.navigationInstruction')
      fields.push('routes.legs.steps.distanceMeters')
      fields.push('routes.legs.steps.duration')
      fields.push('routes.legs.steps.polyline.encodedPolyline')
    }

    // Always include legs for waypoint routes
    if (request.waypoints?.length) {
      fields.push('routes.legs.distanceMeters')
      fields.push('routes.legs.duration')
      fields.push('routes.legs.staticDuration')
      fields.push('routes.legs.startLocation')
      fields.push('routes.legs.endLocation')
    }

    return fields.join(',')
  }

  private validateResponse(
    response: GoogleRoutesResponse,
    request: ComputeRouteRequest
  ): void {
    if (response.error) {
      throw new RoutingError(
        response.error.message,
        response.error.status,
        request.origin,
        request.destination,
        { googleError: response.error }
      )
    }

    if (!response.routes?.length) {
      const fallbackReason = response.fallbackInfo?.reason
      throw new RoutingError(
        fallbackReason
          ? `No route found: ${fallbackReason}`
          : 'No route found between the specified locations',
        'NO_ROUTES',
        request.origin,
        request.destination
      )
    }
  }

  private mapToCanonical(
    route: GoogleRoute,
    request: ComputeRouteRequest
  ): ComputeRouteResult {
    const result: ComputeRouteResult = {
      distanceMeters: route.distanceMeters,
      durationSeconds: this.parseDuration(route.duration),
      source: this.name,
      computedAt: new Date().toISOString(),
    }

    // Static duration (without traffic)
    if (route.staticDuration) {
      result.staticDurationSeconds = this.parseDuration(route.staticDuration)
    }

    // Polyline
    if (route.polyline?.encodedPolyline) {
      result.polyline = route.polyline.encodedPolyline
    }

    // Route description/summary
    if (route.description) {
      result.routeSummary = [route.description]
    }

    // Warnings
    if (route.warnings?.length) {
      result.warnings = route.warnings
    }

    // Toll information
    if (route.travelAdvisory?.tollInfo) {
      result.tolls = this.extractTolls(route.travelAdvisory.tollInfo)
    }

    // Legs (for waypoint routes)
    if (route.legs?.length) {
      result.legs = route.legs.map((leg) => this.mapLeg(leg))
    }

    // Steps (if requested)
    if (request.includeSteps && route.legs?.[0]?.steps) {
      result.steps = route.legs.flatMap((leg) =>
        leg.steps?.map((step) => this.mapStep(step)) ?? []
      )
    }

    return result
  }

  private parseDuration(duration: string): number {
    // Google returns duration as "123s" or "123.456s"
    const match = duration.match(/^(\d+(?:\.\d+)?)s$/)
    if (!match) {
      throw new RoutingError(`Invalid duration format: ${duration}`, 'PARSE_ERROR')
    }
    return Math.round(parseFloat(match[1]))
  }

  private extractTolls(tollInfo: GoogleTollInfo): TollEstimate {
    const prices = tollInfo.estimatedPrice ?? []

    if (prices.length === 0) {
      return {
        amountCents: 0,
        currency: 'USD',
        isComplete: true,
      }
    }

    // Sum up all toll prices (may have multiple currencies)
    const breakdown: TollBreakdownItem[] = []
    let totalCents = 0
    let primaryCurrency = 'USD'
    let hasMultipleCurrencies = false

    for (const price of prices) {
      const cents = this.moneyToCents(price)
      totalCents += cents
      primaryCurrency = price.currencyCode

      // Track if we have multiple currencies
      if (prices.indexOf(price) > 0 && price.currencyCode !== prices[0].currencyCode) {
        hasMultipleCurrencies = true
      }

      breakdown.push({
        name: 'Toll',
        amountCents: cents,
        currency: price.currencyCode,
      })
    }

    return {
      amountCents: totalCents,
      currency: primaryCurrency,
      isComplete: !hasMultipleCurrencies,
      breakdown: breakdown.length > 1 ? breakdown : undefined,
    }
  }

  private moneyToCents(money: GoogleMoney): number {
    const units = parseInt(money.units ?? '0', 10)
    const nanos = money.nanos ?? 0

    // Convert to cents: units * 100 + nanos / 10_000_000
    return Math.round(units * 100 + nanos / 10_000_000)
  }

  private mapLeg(leg: GoogleRouteLeg): RouteLeg {
    const result: RouteLeg = {
      distanceMeters: leg.distanceMeters,
      durationSeconds: this.parseDuration(leg.duration),
      startLocation: leg.startLocation?.latLng ?? { latitude: 0, longitude: 0 },
      endLocation: leg.endLocation?.latLng ?? { latitude: 0, longitude: 0 },
    }

    if (leg.staticDuration) {
      result.staticDurationSeconds = this.parseDuration(leg.staticDuration)
    }

    if (leg.steps) {
      result.steps = leg.steps.map((step) => this.mapStep(step))
    }

    return result
  }

  private mapStep(step: GoogleRouteStep): RouteStep {
    const result: RouteStep = {
      instruction:
        step.navigationInstruction?.instructions ?? 'Continue on route',
      distanceMeters: step.distanceMeters,
      durationSeconds: this.parseDuration(step.duration),
    }

    if (step.polyline?.encodedPolyline) {
      result.polyline = step.polyline.encodedPolyline
    }

    if (step.navigationInstruction?.maneuver) {
      result.maneuver = step.navigationInstruction.maneuver
    }

    return result
  }

  private buildMatrixRequestBody(
    request: DistanceMatrixRequest
  ): Record<string, unknown> {
    return {
      origins: request.origins.map((o) => ({
        waypoint: this.buildWaypoint(o),
      })),
      destinations: request.destinations.map((d) => ({
        waypoint: this.buildWaypoint(d),
      })),
      travelMode: this.mapTravelMode(request.travelMode),
      routingPreference: request.departureTime
        ? 'TRAFFIC_AWARE'
        : 'TRAFFIC_UNAWARE',
      departureTime: request.departureTime
        ? typeof request.departureTime === 'string'
          ? request.departureTime
          : request.departureTime.toISOString()
        : undefined,
      routeModifiers: {
        avoidTolls: request.avoidTolls,
        avoidHighways: request.avoidHighways,
      },
    }
  }

  private mapMatrixToCanonical(
    responses: GoogleRouteMatrixResponse[],
    originCount: number,
    destinationCount: number
  ): DistanceMatrixResult {
    // Initialize result matrix
    const rows: DistanceMatrixResult['rows'] = []
    for (let i = 0; i < originCount; i++) {
      rows.push({
        elements: new Array(destinationCount).fill(null).map(
          (): DistanceMatrixEntry => ({
            distanceMeters: 0,
            durationSeconds: 0,
            status: 'NOT_FOUND',
          })
        ),
      })
    }

    // Fill in from responses
    for (const response of responses) {
      const entry: DistanceMatrixEntry = {
        distanceMeters: response.distanceMeters ?? 0,
        durationSeconds: response.duration
          ? this.parseDuration(response.duration)
          : 0,
        staticDurationSeconds: response.staticDuration
          ? this.parseDuration(response.staticDuration)
          : undefined,
        status:
          response.condition === 'ROUTE_EXISTS'
            ? 'OK'
            : response.condition === 'ROUTE_NOT_FOUND'
              ? 'NOT_FOUND'
              : 'ZERO_RESULTS',
      }

      rows[response.originIndex].elements[response.destinationIndex] = entry
    }

    return {
      rows,
      source: this.name,
      computedAt: new Date().toISOString(),
    }
  }
}
